import PropTypes from "prop-types";
import { twMerge } from "tailwind-merge";
import React, { useState } from "react";
import { SafeFormattedMessage } from "../login/SafeFormattedMessage";

export function TextField({
  show = true,
  error,
  disabled,
  label,
  id,
  value,
  onChange,
  onBlur,
  type = "text",
  wrapperClassName,
  adornment = null,
  inputRef,
  placeholder = "",
  autoFocus = false,
  className = "",
}) {
    if (!show) {
      return null;
    }
    const placeholderSet = Boolean(typeof placeholder === "string" && placeholder.length > 0);
    const baseInputClassName="rounded-base border-1 border-default-medium appearance-none focus:outline-none peer";
    const inputClassName = "block px-2.5 pb-2.5 pt-4 w-full font-normal text-base"
    const ringClassNames = "ring-1 ring-neutral-500 focus:border-blue-600 focus:ring-inherit rounded-lg focus:outline-none"
    const inputColorClassNames = "placeholder-primary-text text-primary-fg bg-transparent";
    
    const baseLabelClassName="absolute font-normal text-base text-primary-text duration-300 transform -translate-y-5 top-2 bg-white peer-focus:mx-0 peer-focus:px-1 peer-focus:text-blue-600 peer-placeholder-shown:scale-75 peer-placeholder-shown:mx-0 px-1 peer-focus:bg-primary-bg2 scale-75";
    const animationClassname = "peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-5 rtl:peer-focus:scale-200 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-focus:translate-x-0 peer-placeholder-shown:translate-x-2 peer-placeholder-shown:top-1/2 peer-placeholder-shown:bg-transparent"

    return (
        <div className={twMerge("relative", wrapperClassName)} >
            <input 
                type={type}
                autoFocus={autoFocus}
                disabled={disabled}
                name={id}
                id={id}
                className={twMerge(baseInputClassName, ringClassNames, inputClassName, inputColorClassNames, error && "ring-error-600 focus:ring-error-400", className)}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                placeholder={placeholder}
                ref={inputRef}
            />
            <label
                htmlFor={id}
                className={twMerge(baseLabelClassName, !placeholderSet && animationClassname, error && "text-error-600 peer-focus:text-error-400", disabled && "text-neutral-400")}
            >
                {label}
        </label>
        <span className="absolute right-2 -translate-y-1/2 top-1/2">
          {adornment}
        </span>

      </div>
    )
}

TextField.propTypes = {
    id: PropTypes.string,
    name: PropTypes.string,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    labelProps: PropTypes.object,
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func.isRequired,
    className: PropTypes.string,
    value: PropTypes.string,
    error: PropTypes.bool,
    helperText: PropTypes.string,
    errorText: PropTypes.string,
    disabled: PropTypes.bool,
    show: PropTypes.bool,
    readOnly: PropTypes.bool,
    type: PropTypes.string,
    inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.object })]),
    adornment: PropTypes.node,
    placeholder: PropTypes.string,
    autoFocus: PropTypes.bool,
    wrapperClassName: PropTypes.string
};

export const TextValidationField = ({
  show = true,
  reportError = () => {},
  clearError = () => {},
  validator,
  match,
  oldText,
  onClick,
  onChange,
  onBlur: onBlurProp,
  onSubmit,
  id,
  name,
  externalValue = "",
  disabled,
  label,
  textType,
  wrapperClassName,
  inputRef,
  autoFocus,
  error = false,
  forceValidateAll = false,
  ...remainingProps
}) => {
  if (!show) {
    return null;
  }

  const [ text, setText ] = useState(externalValue);
  const [ validationError, setValidationError ] = useState(false);

  React.useEffect(() => {
    // External value changes should update internal state, this supports redux state changes
    handleUpdate({
      target: {
        value: externalValue,
      }
    });
  }, [externalValue, name]);

  React.useEffect(() => {
    // When forceValidateAll becomes true (e.g. on submit attempt), re-validate immediately
    // so errors appear without requiring a keystroke.
    if (forceValidateAll) {
      validateAndReportErrors(text, true);
    }
  }, [forceValidateAll]);

  const validateAndReportErrors = (newText, shouldShowAll = false) => {
    let newValidationErrors = [];

    if (validator) {
      newValidationErrors.push(...validator.validateFormat(newText));
      newValidationErrors.push(...validator.validateRules(newText, match, oldText));
    }

    // Separate errors by severity
    const fatalErrors = newValidationErrors.filter(err => err.severity === "fatal");
    
    // Determine which errors to display to user based on forceValidateAll flag
    const errorsToShow = (shouldShowAll || forceValidateAll)
      ? newValidationErrors 
      : fatalErrors;
    
    // Always report all validation errors to parent (for submit gate),
    // but only display the appropriate subset to the user
    if (errorsToShow.length > 0) {
      reportError(id, errorsToShow.map((err, idx) => <SafeFormattedMessage key={idx} {...err.message} values={err.context} />));
      setValidationError(true);
    } else if (newValidationErrors.length > 0) {
      // Soft errors exist but are not shown yet — report empty to parent so it
      // can block submission, but don't display outline or messages.
      reportError(id, []);
      setValidationError(false);
    } else {
      clearError(id);
      setValidationError(false);
    }

    return {
      isValid: newValidationErrors.length === 0 && newText.length > 0,
      allErrors: newValidationErrors,
      errorsShown: errorsToShow
    };
  };

  const handleUpdate = (event) => {
    const newText = event.target.value;
    
    // While typing, only force soft-error display after explicit submit attempts.
    const validationResult = validateAndReportErrors(newText, forceValidateAll);
    
    setText(newText);

    onChange({
      target: {
        name: name,
        id: id,
        value: newText,
        valid: validationResult.isValid
      }
    });
  };

  const handleBlur = (event) => {
    // Re-validate and show all errors on blur
    const newText = text;
    validateAndReportErrors(newText, true);
    
    if (onBlurProp) {
      onBlurProp(event);
    }
  };

  const bespokeWrapperClassName = "transition duration-900 focus-within:border-b-primary-500";
        
  return (
        <TextField
          {...remainingProps}
          autoFocus={autoFocus}
          id={id}
          disabled={disabled}
          type={textType}
          name={name}
          onChange={handleUpdate}
          onClick={onClick}
          onBlur={handleBlur}
          label={label}
          value={text}
          error={error || validationError}
          onSubmit={onSubmit}
          wrapperClassName={twMerge(bespokeWrapperClassName, wrapperClassName)}
          inputRef={inputRef}
        />
  )
}

TextValidationField.propTypes = {
  adornment: PropTypes.node,
  autoFocus: PropTypes.bool,
  forceValidateAll: PropTypes.bool,
  validator: PropTypes.shape({
    validateFormat: PropTypes.func.isRequired,
    validateRules: PropTypes.func.isRequired
  }),
  match: PropTypes.string,
  oldText: PropTypes.string,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  onBlur: PropTypes.func,
  onSubmit: PropTypes.func,
  id: PropTypes.string,
  name: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  show: PropTypes.bool,
  label: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.func,
    PropTypes.string,
  ]),
  externalValue: PropTypes.string,
  textType: PropTypes.oneOf(["text", "password"]),
  reportError: PropTypes.func,
  clearError: PropTypes.func,
  wrapperClassName: PropTypes.string,
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.object })]),
}
